import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { CTASection } from '../../shared/components/CTASection';
import { ProductCard } from '../../shared/components/ProductCard';
import { SectionHeading } from '../../shared/components/SectionHeading';
import { getProductBySlug } from '../../shared/data/products';
import { PageShell } from '../PageShell/index';

export function PigFeed() {
  const product = getProductBySlug('pig-feed')!;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Why is stage-wise feeding important for pigs?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Pigs have very different nutritional needs at each life stage. Feed must transition in protein, lysine, and energy content to support growth phases efficiently.'
        }
      },
      {
        '@type': 'Question',
        'name': 'How do I calculate feed dosage for my pigs?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Daily feed intake ranges from 0.5 kg for piglets to 2.5 kg or more for market finishers. Use our Pig Weight Calculator to estimate animal weight and plan rations.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Is ZFAST Pig Feed available for bulk order?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes, we support bulk supply and dealership distribution. Please contact our team on the Dealer Partnership page.'
        }
      }
    ]
  };

  return (
    <PageShell
      title="Pig Feed"
      eyebrow="Animal Nutrition"
      path="/animal-nutrition/pig-feed"
      description="Pig feed is positioned as a stage-wise swine nutrition product page for farm routines, breeder conversations, grower needs, and dealer supply."
      breadcrumbs={[{ label: 'Animal Nutrition', href: '/animal-nutrition' }, { label: 'Pig Feed' }]}
      jsonLd={faqSchema}
    >
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <ProductCard product={product} />
          <div className="rounded-[1.4rem] bg-cream p-8 flex flex-col justify-between">
            <div>
              <img src="/images/products/pig-feed/zfast-pig-feed.webp" alt="Swine herd feeding at modern pig farm" loading="eager" width={500} height={256} className="mb-8 h-64 w-full rounded-[1.15rem] object-cover" />
              <SectionHeading title="Stage-wise feed support">
                <p>Zfast pig feed supports practical stage-wise nutrition, consistent intake routines, breeder planning, grower management, and dealer supply conversations.</p>
              </SectionHeading>
            </div>
            <div className="mt-8 grid gap-3">
              {product.suitableFor.map((item: string) => (
                <p key={item} className="rounded-2xl bg-white p-4 font-bold text-charcoal/75 shadow-sm border border-forest/5 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-forest shrink-0" />
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stage-wise Guidance Details */}
      <section className="bg-white py-16 border-t border-forest/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-black text-ink">Stage-Wise Feeding Programs</h3>
            <p className="mt-4 text-charcoal/70">Proper nutritional feed distribution across the life cycle of pigs.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="p-6 rounded-2xl bg-cream border border-forest/5">
              <span className="text-xs font-black uppercase text-gold">Stage 1</span>
              <h4 className="mt-2 text-xl font-black text-ink">Pre-Starter Feed</h4>
              <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">Formulated for piglets from weaning up to 10 kg. Designed with highly digestible ingredients to ease transitional stress.</p>
            </div>
            <div className="p-6 rounded-2xl bg-cream border border-forest/5">
              <span className="text-xs font-black uppercase text-gold">Stage 2</span>
              <h4 className="mt-2 text-xl font-black text-ink">Starter Feed</h4>
              <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">Balanced protein and essential energy for young pigs weighing 10 kg to 25 kg. Promotes bone strength and muscle support.</p>
            </div>
            <div className="p-6 rounded-2xl bg-cream border border-forest/5">
              <span className="text-xs font-black uppercase text-gold">Stage 3</span>
              <h4 className="mt-2 text-xl font-black text-ink">Grower Feed</h4>
              <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">High-energy and highly digestible feed for growing pigs from 25 kg to 50 kg. Promotes daily weight gain and feed efficiency.</p>
            </div>
            <div className="p-6 rounded-2xl bg-cream border border-forest/5">
              <span className="text-xs font-black uppercase text-gold">Stage 4</span>
              <h4 className="mt-2 text-xl font-black text-ink">Finisher Feed</h4>
              <p className="mt-3 text-sm text-charcoal/70 leading-relaxed">Specially balanced energy-to-protein ratio for swine from 50 kg to market weight. Supports optimal carcass quality.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pig Feed FAQs */}
      <section className="bg-cream py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-black text-ink">Frequently Asked Questions</h3>
            <p className="mt-4 text-charcoal/70">Get answers to common swine nutrition and feed management questions.</p>
          </div>
          <div className="space-y-6">
            <div className="rounded-2xl bg-white p-6 shadow-sm border border-forest/5">
              <h4 className="font-black text-ink text-lg">Why is stage-wise feeding important for pigs?</h4>
              <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">
                Pigs have very different nutritional needs at each life stage. Feed must transition in protein, amino acids (lysine), and energy content to support growth phases efficiently without nutritional waste.
              </p>
            </div>
            <div className="rounded-2xl bg-white p-6 shadow-sm border border-forest/5">
              <h4 className="font-black text-ink text-lg">How do I calculate feed dosage for my pigs?</h4>
              <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">
                Daily feed intake ranges from 0.5 kg for starter piglets up to 2.5 kg or more for market finishers. We recommend using our <Link to="/calculators/pig" className="text-forest font-bold hover:underline">pig weight calculator</Link> to estimate weight and plan daily feeding rations.
              </p>
            </div>
            <div className="rounded-2xl bg-white p-6 shadow-sm border border-forest/5">
              <h4 className="font-black text-ink text-lg">Is ZFAST Pig Feed available for bulk order?</h4>
              <p className="mt-3 text-charcoal/70 text-sm leading-relaxed">
                Yes, ZFAST Pig Feed is fully available for bulk farm delivery and dealer distribution. Please submit your specifications through our <Link to="/dealer-partnership" className="text-forest font-bold hover:underline">ZFAST dealer partnership</Link> form.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Need custom feed guidelines?" description="Let our nutrition specialists recommend the ideal feed plan based on your swine stock size." primaryLabel="Get Support" primaryHref="/contact" />
    </PageShell>
  );
}
